import React, { createContext, useCallback, useContext, useEffect, useRef, useState } from 'react';
import type { ReactNode } from 'react';
import { randomUUID } from '@ag-ui/client';
import type { Message } from '@ag-ui/client';
import { useQueryClient } from '@tanstack/react-query';
import { prefetchChunks } from '../citations/chunks.hooks';
import type { CreatePandaAgent, PandaHttpAgent } from '../agent/panda-agent';
import { fetchThreadDetail } from '../agent/panda-threads.api';
import { AG_UI_ROLE, HUB_SOURCE, isHubSource } from '../agent/ag-ui.constants';
import {
  hubAnswerMessageId,
  hubOfferResponse,
  lastAssistantMessage,
  trailingFallbackOffer,
} from '../messages/hub-offer';
import {
  hubAnswersFrom,
  seedHubAnswers,
  tagHubAnswer,
  withHubSources,
} from '../messages/hub-answer';
import type { HubAnswers } from '../messages/hub-answer';
import { executeRun, resumeRun } from './panda-run-lifecycle';
import type { RefreshOutcome, RunLifecycleDeps } from './panda-run-lifecycle';
import { useLang } from '../../hooks/lang.hooks';
import { usePandaChatStore } from './panda-chat-store.hooks';
import { takeHandoff, toHandoffMessages } from '../handoff-bus';
import type { Handoff } from '../handoff-bus';
import { SEARCH_ANSWER_STATUS } from '../search-answer/search-answer.constants';
import authService, { REFRESH_RESULT } from '../../services/auth.service';
import { conversationDepth, hubAnalyticsType } from '../analytics/panda-analytics';
import type { PandaAnalytics } from '../analytics/panda-analytics';
import type { PandaChatErrorKind, PandaConversation } from '../panda-chat.types';
import { acceptOffer } from './panda-offer-accept';

const PandaChatContext = createContext<PandaConversation | null>(null);

interface PendingAccept {
  agent: PandaHttpAgent;
  offerId: string;
  chunkIds: string[];
}

/** The offer accept a handoff asks the chat to run itself, or `null`. */
function toPendingAccept(handoff: Handoff | null, agent: PandaHttpAgent): PendingAccept | null {
  if (handoff?.kind !== 'complete' || !handoff.acceptOffer) return null;
  const offer = trailingFallbackOffer(handoff.messages);
  return offer && { agent, offerId: offer.offerId, chunkIds: offer.chunkIds };
}

/** Access the Panda (V2) chat conversation context. */
export function usePandaConversation(): PandaConversation {
  const context = useContext(PandaChatContext);
  if (!context) {
    throw new Error('usePandaConversation must be used within a PandaChatProvider');
  }
  return context;
}

interface RetryContext {
  agent: PandaHttpAgent;
  messagesBeforeRun: Message[];
  userMessageId: string;
}

interface PandaChatProviderBaseProps {
  children: ReactNode;
  /** Platform agent build, with its fetch/auth adapter baked in. */
  createAgent: CreatePandaAgent;
  /** Platform analytics, bound to that host's tracker. */
  analytics: PandaAnalytics;
  /** Seeds a freshly-mounted provider with persisted history. */
  initialMessages?: Message[];
  /** Called with the full message list whenever a run settles, success or failure. */
  onRunSettled?: (messages: Message[]) => void;
  /** Called with the user-facing error message when a run fails. */
  onError?: (message: string) => void;
  /** Refresh the session and re-run once when a stream fails with 401. */
  recoverUnauthorized?: boolean;
}

/**
 * Panda (V2) conversation React shell shared by web and mobile. The run itself
 * lives in the React-free panda-run-lifecycle; platform differences are
 * injected via props by each host's thin PandaChatProvider.
 */
export function PandaChatProviderBase({
  children,
  createAgent,
  analytics,
  initialMessages,
  onRunSettled,
  onError,
  recoverUnauthorized = true,
}: PandaChatProviderBaseProps) {
  const {
    conversationId,
    isOpen,
    isChatEnabled,
    handoffId,
    setConversationId,
    clearConversation,
    clearHandoff,
    logout,
  } = usePandaChatStore();
  const lang = useLang('AI_CHAT');
  const queryClient = useQueryClient();

  // One long-lived AG-UI agent per conversation; `agent.messages` is the source
  // of truth. The store persists threadId so remounts resume the thread.
  const [agent, setAgent] = useState(() =>
    createAgent({ threadId: conversationId || undefined, initialMessages }),
  );
  const [isSending, setIsSending] = useState(false);
  // Held across an offer accept incl. its retry — startRun alone would unlock the
  // composer between attempts and let a send collide with the retry.
  const [acceptInFlight, setAcceptInFlight] = useState(false);
  const busy = isSending || acceptInFlight;
  const [error, setError] = useState<false | PandaChatErrorKind>(false);
  const [reconnecting, setReconnecting] = useState(false);
  // Id of an ASA offer whose accept was rejected outright (retry exhausted). The
  // offer's own message renders a failure line in place of the button.
  const [failedOfferId, setFailedOfferId] = useState<string | null>(null);
  // Re-render on every agent.messages mutation (fires down to each streamed token).
  const [, forceRender] = useState(0);

  // Identifies the run that owns the conversation. Each run keeps the value it
  // started with and stops reporting once this ref no longer holds it.
  const runTokenRef = useRef<object>({});
  // Off-screen agent currently re-attaching; clearChat must abort it too.
  const shadowRef = useRef<PandaHttpAgent | null>(null);
  // What the Retry button re-runs: the turn that failed, as it failed.
  const retryRef = useRef<RetryContext | null>(null);
  // Hub answers keyed to their source (e.g. ASA), one per run. Live, the marker
  // rides on the run's state (knowledge_hub_generation) and we tag the run's
  // latest assistant message as it streams; persisted messages carry it as
  // `knowledgeHub`, so a remounted provider seeds from them.
  const hubAnswersRef = useRef<HubAnswers>(hubAnswersFrom(initialMessages ?? []));
  // Server error code from the most recent run — drives the offer-accept retry.
  const lastRunErrorCodeRef = useRef<string | undefined>(undefined);
  // A handed-off offer accept, parked until its agent's subscription is attached.
  const pendingAcceptRef = useRef<PendingAccept | null>(null);
  // The accept in flight; resetToAgent rotates it so a mid-retry accept goes quiet.
  const acceptSessionRef = useRef<object | null>(null);

  useEffect(() => {
    // A swapped-in agent (recovery) arrives with persisted messages — pick up their markers.
    seedHubAnswers(hubAnswersRef.current, agent.messages);
    const markHubAnswerFromState = () => {
      const generation = (
        agent.state as
          | { knowledge_hub_generation?: { source?: string; offer_id?: string } }
          | undefined
      )?.knowledge_hub_generation;
      const source = generation?.source;
      if (!isHubSource(source)) return;
      const messageId = hubAnswerMessageId(agent.messages);
      if (messageId) {
        tagHubAnswer(hubAnswersRef.current, { offerId: generation?.offer_id, messageId, source });
      }
    };
    const { unsubscribe } = agent.subscribe({
      onMessagesChanged: () => {
        markHubAnswerFromState();
        forceRender(n => n + 1);
      },
    });
    return unsubscribe;
  }, [agent]);

  const hubSourceOf = useCallback(
    (messageId: string) => hubAnswersRef.current.sources.get(messageId),
    [],
  );

  const startRun = useCallback(
    async ({
      resume,
      retryOfOwnRun,
      suppressServerErrorBanner,
      ...context
    }: RetryContext & {
      resume?: boolean;
      retryOfOwnRun?: boolean;
      suppressServerErrorBanner?: boolean;
    }) => {
      const { agent: runAgent, messagesBeforeRun, userMessageId } = context;
      setError(false);
      setIsSending(true);

      const token = {};
      runTokenRef.current = token;

      const deps: RunLifecycleDeps<PandaHttpAgent> = {
        agent: runAgent,
        createAgent,
        // Recovery baseline: shadow agents replay the run on top of this snapshot.
        messagesBeforeRun,
        userMessageId,
        cancelled: () => runTokenRef.current !== token,
        attachOnRunConflict: retryOfOwnRun,
        recoverUnauthorized,
        refreshSession: async (): Promise<RefreshOutcome> => {
          // refreshToken() never rejects — it resolves with a REFRESH_RESULT.
          const result = await authService.refreshToken();
          if (result === REFRESH_RESULT.SUCCESS) return 'success';
          if (result === REFRESH_RESULT.AUTH_FAILED) return 'auth_failed';
          return 'network_error';
        },
        fetchThread: fetchThreadDetail,
        onAgentSwap: setAgent,
        onMessagesChanged: () => forceRender(n => n + 1),
        onReconnectingChange: setReconnecting,
        onShadowChange: shadow => {
          shadowRef.current = shadow;
        },
        // Settle with a snapshot — messages are live and mutated in place by later turns.
        onSettled: settledAgent => {
          retryRef.current = null;
          // The lane's decision on offering, reported once per run that ran it.
          const response = hubOfferResponse(settledAgent.state);
          const responseMessage = lastAssistantMessage(settledAgent.messages);
          if (response && responseMessage) {
            analytics.trackFallbackResponse({
              conversationId: conversationId ?? settledAgent.threadId,
              messageId: responseMessage.id,
              type: hubAnalyticsType(HUB_SOURCE.ASA),
              response,
            });
          }
          onRunSettled?.(withHubSources([...settledAgent.messages], hubAnswersRef.current.sources));
        },
        onFailed: (kind, failedAgent) => {
          retryRef.current = { agent: failedAgent, messagesBeforeRun, userMessageId };
          // Persist whatever streamed before the failure so the turn isn't lost.
          onRunSettled?.(withHubSources([...failedAgent.messages], hubAnswersRef.current.sources));
          setError(kind);
          const copy =
            kind === 'connection'
              ? `${lang.CONNECTION_LOST_TITLE} — ${lang.CONNECTION_LOST_SUBTITLE}`
              : lang.ERROR_MESSAGE;
          onError?.(`${lang.BOT_NAME}: ${copy}`);
        },
        onRunError: code => {
          lastRunErrorCodeRef.current = code;
        },
        suppressServerErrorBanner,
        onAuthFailed: logout,
      };

      try {
        if (resume) await resumeRun<PandaHttpAgent>(deps);
        else await executeRun<PandaHttpAgent>(deps);
      } finally {
        // Only the run that still owns the conversation may unlock the composer.
        if (runTokenRef.current === token) setIsSending(false);
      }
    },
    [
      createAgent,
      recoverUnauthorized,
      onRunSettled,
      onError,
      logout,
      lang,
      analytics,
      conversationId,
    ],
  );

  const onMessageSend = useCallback(
    async ({ message }: { message: string }) => {
      if (busy) return;
      const trimmed = message?.trim();
      if (!trimmed) return;

      if (!conversationId) setConversationId(agent.threadId);

      // Append the user turn via the client's mutator so the bubble renders before the stream.
      const messageId = randomUUID();
      agent.addMessage({ id: messageId, role: AG_UI_ROLE.USER, content: trimmed });

      analytics.trackMessageSent({
        // The store's copy is still null on the first send of a thread.
        conversationId: conversationId ?? agent.threadId,
        messageId,
        depth: conversationDepth(agent.messages),
      });

      await startRun({ agent, messagesBeforeRun: [...agent.messages], userMessageId: messageId });
    },
    [busy, agent, conversationId, analytics, setConversationId, startRun],
  );

  // Accept a knowledge-hub (ASA) offer: echo its tool-call id back as a tool
  // message so the server re-reads the persisted offer and routes straight to
  // hub generation. Same append-then-run shape as onMessageSend.
  const acceptHubOffer = useCallback(
    async (targetAgent: PandaHttpAgent, offerId: string, chunkIds: string[]) => {
      const session = {};
      acceptSessionRef.current = session;
      setFailedOfferId(null);
      // Warm the citation chunks in parallel with generation so they're ready on arrival.
      prefetchChunks(queryClient, chunkIds);
      // The offer tool message is the run's last message, so a retry just re-runs it.
      const messageId = randomUUID();
      targetAgent.addMessage({
        id: messageId,
        role: AG_UI_ROLE.TOOL,
        toolCallId: offerId,
        content: '{}',
      });

      const runOnce = () => {
        lastRunErrorCodeRef.current = undefined;
        // A rejection surfaces as a code, not the generic error banner — a rejected
        // accept isn't a system failure; we show an offer-specific line instead.
        return startRun({
          agent: targetAgent,
          messagesBeforeRun: [...targetAgent.messages],
          userMessageId: messageId,
          suppressServerErrorBanner: true,
        });
      };

      // The accept holds the composer across its retry (startRun unlocks it after
      // each run); a rejection after the retry shows the ASA failure line.
      const rejected = await acceptOffer({
        run: runOnce,
        lastErrorCode: () => lastRunErrorCodeRef.current,
        isCancelled: () => acceptSessionRef.current !== session,
        // A superseded accept's teardown must not unlock the composer mid-retry of its successor.
        setInFlight: inFlight => {
          if (acceptSessionRef.current === session) setAcceptInFlight(inFlight);
        },
        sleep: ms => new Promise<void>(resolve => setTimeout(() => resolve(), ms)),
      });
      if (rejected) {
        setFailedOfferId(offerId);
      }
    },
    [startRun, queryClient],
  );

  const onOfferHubAnswer = useCallback(
    async (offerId: string, chunkIds: string[]) => {
      if (busy) return;
      await acceptHubOffer(agent, offerId, chunkIds);
    },
    [busy, agent, acceptHubOffer],
  );

  // Must run after the subscription effect: a run only notifies subscribers that existed when it started.
  useEffect(() => {
    const pending = pendingAcceptRef.current;
    if (!pending || pending.agent !== agent) return;
    pendingAcceptRef.current = null;
    void acceptHubOffer(agent, pending.offerId, pending.chunkIds);
  }, [agent, acceptHubOffer]);

  const isOfferFailed = useCallback(
    (offerId: string) => failedOfferId === offerId,
    [failedOfferId],
  );

  // Abort anything in flight and hand the conversation over to `nextAgent`.
  const resetToAgent = useCallback(
    (nextAgent: PandaHttpAgent) => {
      // Rotate the token first so the aborted run's continuation goes quiet.
      runTokenRef.current = {};
      agent.abortRun?.();
      shadowRef.current?.abortRun?.();
      shadowRef.current = null;
      retryRef.current = null;
      pendingAcceptRef.current = null;
      acceptSessionRef.current = null;
      setAgent(nextAgent);
      setError(false);
      setReconnecting(false);
      setFailedOfferId(null);
      setAcceptInFlight(false);
      setIsSending(false);
    },
    [agent],
  );

  // On org switch / clear, the store drops conversationId; if the agent still
  // holds messages, start a fresh one so the two don't diverge. Through
  // resetToAgent, so a parked or retrying offer accept dies with the agent.
  useEffect(() => {
    if (!isSending && !conversationId && agent.messages.length > 0) {
      resetToAgent(createAgent());
    }
  }, [conversationId, isSending, agent.messages.length, createAgent, resetToAgent]);

  // Handoff from the search card: show that exchange, replacing whatever the
  // chat held. The thread stays client-side until the user asks something.
  // An already-adopted id means a repeat "show more" — keep the conversation.
  const lastHandoffIdRef = useRef<string | null>(null);

  useEffect(() => {
    if (!handoffId) return;
    // Clearing first makes re-runs from the other deps no-ops.
    clearHandoff();
    // Already this conversation (repeat or remount): keep it and re-persist what the handoff dropped.
    const holdsConversation = handoffId === agent.threadId && agent.messages.length > 0;
    if (handoffId === lastHandoffIdRef.current || holdsConversation) {
      lastHandoffIdRef.current = handoffId;
      onRunSettled?.(withHubSources([...agent.messages], hubAnswersRef.current.sources));
      return;
    }
    lastHandoffIdRef.current = handoffId;

    const handoff = takeHandoff(handoffId);

    // A live handoff: the search run is still streaming. Adopt its agent so the
    // answer finishes on this screen, then swap to a chat agent seeded with the
    // completed messages — a follow-up must run against the thread endpoint, not
    // the one-shot search one. isSending stays true meanwhile, locking the
    // composer as any streaming turn does.
    if (handoff?.kind === 'live') {
      const { live } = handoff;
      resetToAgent(live.agent);
      const token = runTokenRef.current;
      setIsSending(true);
      // Re-render on the run's own per-token signal: the adopted run started on
      // the search screen, so agent.subscribe() here (added after the run began)
      // gets no events until it ends — without this the whole answer lands at once.
      const unsubscribe = live.subscribe(() => forceRender(n => n + 1));
      void live.settled.then(status => {
        unsubscribe();
        // A clear/new handoff since then rotated the token — leave their agent be.
        if (runTokenRef.current !== token) return;
        const messages = toHandoffMessages(live.agent.messages);
        setAgent(
          createAgent({
            threadId: handoffId,
            initialMessages: messages,
            parentRunId: handoff.parentRunId,
          }),
        );
        onRunSettled?.(messages);
        // The card shows an error line for a failed or hidden run; adopted, it
        // would otherwise read as a finished answer. A fallback offer is a real ending.
        if (status !== SEARCH_ANSWER_STATUS.DONE && status !== SEARCH_ANSWER_STATUS.SUGGESTION) {
          setError('generic');
        }
        setIsSending(false);
      });
      return;
    }

    // Static handoff (answer already complete, or the live run was lost): seed
    // from the snapshot the card handed over. `initialMessages` covers a bus
    // entry already taken (double-fired effect) on hosts that persist.
    const nextAgent = createAgent({
      threadId: handoffId,
      initialMessages: handoff?.messages ?? initialMessages,
      parentRunId: handoff?.parentRunId,
    });
    resetToAgent(nextAgent);
    // A handed-off exchange has no settling run, so persist it here.
    if (handoff) onRunSettled?.(handoff.messages);
    pendingAcceptRef.current = toPendingAccept(handoff, nextAgent);
  }, [handoffId, agent, clearHandoff, resetToAgent, createAgent, initialMessages, onRunSettled]);

  // Retry: a mid-stream failure resumes the server-side run; a failure before
  // anything streamed re-sends the question.
  const onRetry = useCallback(() => {
    const context = retryRef.current;
    if (!context || busy) return;
    const resume = context.agent.messages.length > context.messagesBeforeRun.length;
    void startRun({ ...context, resume, retryOfOwnRun: true });
  }, [busy, startRun]);

  const clearChat = useCallback(() => {
    analytics.trackClearChat({ conversationId, depth: conversationDepth(agent.messages) });
    resetToAgent(createAgent());
    clearConversation();
  }, [conversationId, agent, analytics, clearConversation, createAgent, resetToAgent]);

  const value: PandaConversation = {
    conversationId,
    loading: busy,
    messages: agent.messages,
    error,
    reconnecting,
    lang,
    isOpen,
    isChatEnabled,
    onMessageSend,
    onOfferHubAnswer,
    hubSourceOf,
    isOfferFailed,
    onRetry,
    clearChat,
  };

  return <PandaChatContext.Provider value={value}>{children}</PandaChatContext.Provider>;
}
